import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

import { logger } from '@/utils/logger';
import { INTERNATIONAL_LOCATION_ID } from '@/constants/config';
import { BRAZIL_LOCATION_ID } from '@/constants/branding';
import {
  clearCrossDomainLocationOverride,
  createCrossDomainLocationState,
  getCrossDomainLocationOverride,
  USER_LOCATION_STORAGE_KEY,
  USER_LOCATION_STORAGE_VERSION,
  USER_LOCATION_STORAGE_VERSION_KEY,
} from '@/lib/crossDomainState';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';

// Single source of truth storage key
const STORAGE_KEY = USER_LOCATION_STORAGE_KEY;

export type IntentMode = 'local' | 'destination' | 'international';

// Unified location state structure
// NOTE: location now stores location_id (string) instead of iso_alpha2
// 'navigation' source means browsing via URL, not a permanent user choice
interface LocationState {
  location: string; // location_id or INTERNATIONAL_LOCATION_ID ('0')
  source: 'user_selection' | 'account' | 'ip' | 'default' | 'navigation';
  intentMode: IntentMode;
  targetLocation: string | null;
}

interface UserContextType {
  currentLocation: string; // location_id or INTERNATIONAL_LOCATION_ID ('0')
  targetLocation: string | null;
  intentMode: IntentMode;
  detectedLocation: string | null;
  isLoading: boolean;
  locationSource: LocationState['source'];
  setCurrentLocation: (location: string) => void;
  setTargetLocation: (location: string | null) => void;
  setIntentMode: (mode: IntentMode) => void;
  updateLocation: (location: string, source?: LocationState['source']) => void;
}

const defaultState: LocationState = {
  location: INTERNATIONAL_LOCATION_ID,
  source: 'default',
  intentMode: 'local',
  targetLocation: null,
};

const UserContext = createContext<UserContextType | undefined>(undefined);

// Safe default context for when used outside provider (e.g., during hot reload)
const safeDefaultContext: UserContextType = {
  currentLocation: INTERNATIONAL_LOCATION_ID,
  targetLocation: null,
  intentMode: 'local',
  detectedLocation: null,
  isLoading: false, // Don't block rendering when outside provider
  locationSource: 'default',
  setCurrentLocation: () => {},
  setTargetLocation: () => {},
  setIntentMode: () => {},
  updateLocation: () => {},
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  // Return safe default instead of throwing (handles hot-reload edge cases)
  if (!context) {
    return safeDefaultContext;
  }
  return context;
};

// Storage version - increment when schema changes to force reset
// v3: Changed from iso_alpha2 to location_id
// v4: Changed GLOBAL location_id from 'GLOBAL' to '0'
const STORAGE_VERSION = USER_LOCATION_STORAGE_VERSION;
const STORAGE_VERSION_KEY = USER_LOCATION_STORAGE_VERSION_KEY;

/**
 * Converts external iso_alpha2 code to internal location_id.
 * 
 * VALID USE: This is the ONLY place where querying by iso_alpha2 is correct,
 * because we receive ISO codes from external sources:
 * - IP geolocation API returns country as iso_alpha2
 * - Google Maps API returns country codes as iso_alpha2
 * - Legacy localStorage data migration (iso_alpha2 → location_id)
 * 
 * After conversion, ALL internal operations use location_id.
 * @see eslint-rules/data-layer/no-iso-alpha2-lookup.cjs
 */
const convertIsoToLocationId = async (isoCode: string): Promise<string> => {
  if (!isoCode || isoCode === 'GLOBAL' || isoCode === INTERNATIONAL_LOCATION_ID) return INTERNATIONAL_LOCATION_ID;
  
  try {
    const { data } = await supabase
      .from('locations')
      .select('location_id')
      .eq('iso_alpha2', isoCode)
      .maybeSingle();
    
    return data?.location_id?.toString() || INTERNATIONAL_LOCATION_ID;
  } catch {
    return INTERNATIONAL_LOCATION_ID;
  }
};

// Helper to load state from localStorage
const loadStoredState = (): LocationState | null => {
  try {
    // Check version and clear if outdated
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    if (storedVersion !== STORAGE_VERSION) {
      logger.debug('[Location] Clearing outdated localStorage, version:', storedVersion, '→', STORAGE_VERSION);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
      return null;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      logger.debug('[Location] Loaded stored state:', parsed);
      return parsed;
    }
  } catch (error) {
    logger.error('[Location] Error loading stored state:', error);
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

// Helper to save state to localStorage
const saveState = (state: LocationState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or unavailable
  }
};

// Helper to track location detection
const trackLocationDetection = (
  location: string,
  source: LocationState['source'],
  metadata?: Record<string, unknown>
) => {
  const sessionId = sessionStorage.getItem('analytics_session_id');
  supabase.functions.invoke('track-analytics-event', {
    body: {
      eventType: 'location_detection',
      eventCategory: 'navigation',
      sourcePage: window.location.pathname,
      sessionId,
      metadata: { location, detectionSource: source, ...metadata }
    }
  }).catch(() => {}); // Silent fail
};

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  
  const [state, setState] = useState<LocationState>(defaultState);
  const [detectedLocation, setDetectedLocation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize location on mount - wait for auth to complete first
  useEffect(() => {
    // Don't run until auth has finished loading
    if (authLoading) return;

    const initializeLocation = async () => {
      setIsLoading(true);
      
      try {
        const crossDomainLocation = getCrossDomainLocationOverride();
        if (crossDomainLocation) {
          const newState = createCrossDomainLocationState(crossDomainLocation);
          setState(newState);
          saveState(newState);
          clearCrossDomainLocationOverride();
          return;
        }

        // 1. Check stored state first (single source of truth)
        const storedState = loadStoredState();
        
        // If user has made an explicit selection, use it — BUT on tributario.com.br,
        // only honor it if the selection is Brazil (compatible with domain contract)
        if (storedState?.source === 'user_selection') {
          if (DOMAIN_DEFAULTS.isTributario && storedState.location !== BRAZIL_LOCATION_ID) {
            logger.info('[Location] Tributario: ignoring non-Brazil user_selection', { location: storedState.location });
            // Fall through to tributario domain default below
          } else {
            logger.debug('[Location] Using user selection:', storedState.location);
            setState(storedState);
            return;
          }
        }
        
        // 1.5. On tributario.com.br, domain is SOT — always Brazil
        //      Only user_selection (already handled at step 1) beats domain default
        if (DOMAIN_DEFAULTS.isTributario) {
          logger.debug('[Location] Domain is tributario.com.br, enforcing Brazil');
          const newState: LocationState = {
            location: BRAZIL_LOCATION_ID,
            source: 'default',
            intentMode: 'local',
            targetLocation: null,
          };
          setState(newState);
          saveState(newState);
          trackLocationDetection(BRAZIL_LOCATION_ID, 'default', { reason: 'tributario_domain' });
          return;
        }

        // 2. Check authenticated user's account settings
        if (user?.id) {
          try {
            const { data: userData } = await supabase
              .from('users')
              .select('location_id')
              .eq('id', user.id)
              .maybeSingle();
            
            if (userData?.location_id) {
              const locationId = userData.location_id.toString();
              logger.debug('[Location] Using account location_id:', locationId);
              const newState: LocationState = {
                location: locationId,
                source: 'account',
                intentMode: storedState?.intentMode || 'local',
                targetLocation: storedState?.targetLocation || null,
              };
              setState(newState);
              saveState(newState);
              trackLocationDetection(locationId, 'account');
              return;
            }
          } catch (error) {
            logger.warn('[Location] Failed to fetch account country:', error);
          }
        }
        
        // 3. Use stored state if it exists
        if (storedState) {
          logger.debug('[Location] Using stored state:', storedState.location, 'source:', storedState.source);
          setState(storedState);
          return;
        }
        
        // 4. First visit - default to INTERNATIONAL (user can select their location explicitly)
        logger.debug('[Location] First visit, defaulting to INTERNATIONAL');
        setState(defaultState);
        saveState(defaultState);
      } catch (error) {
        logger.error('[Location] Initialization failed:', error);
        setState(defaultState);
        saveState(defaultState);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLocation();
  }, [user?.id, authLoading]);

  // Update location with optional source (defaults to 'user_selection')
  const updateLocation = useCallback((location: string, source: LocationState['source'] = 'user_selection') => {
    setState(prevState => {
      // Don't override user_selection with navigation - preserve explicit user choice
      const effectiveSource = prevState.source === 'user_selection' && source === 'navigation'
        ? 'user_selection'
        : source;
      
      const newState: LocationState = {
        ...prevState,
        location,
        source: effectiveSource,
      };
      
      // Only persist to localStorage if it's a real user choice
      if (source === 'user_selection') {
        saveState(newState);
      }
      
      return newState;
    });
  }, []);

  const setCurrentLocation = useCallback((location: string) => {
    updateLocation(location);
  }, [updateLocation]);

  const setTargetLocation = useCallback((targetLocation: string | null) => {
    setState(prevState => {
      const newState: LocationState = {
        ...prevState,
        targetLocation,
      };
      saveState(newState);
      return newState;
    });
  }, []);

  const setIntentMode = useCallback((intentMode: IntentMode) => {
    setState(prevState => {
      const newState: LocationState = {
        ...prevState,
        intentMode,
      };
      saveState(newState);
      return newState;
    });
  }, []);

  const value: UserContextType = {
    currentLocation: state.location,
    targetLocation: state.targetLocation,
    intentMode: state.intentMode,
    detectedLocation,
    isLoading,
    locationSource: state.source,
    setCurrentLocation,
    setTargetLocation,
    setIntentMode,
    updateLocation,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
